import {
  type Wallet as StandardWallet,
  getWallets,
} from "@wallet-standard/core";
import { useEffect, useState } from "react";

type UseWalletsOptions<T> = {
  filterWallets: (wallets: readonly StandardWallet[]) => T[];
  getExtraWallets?: () => StandardWallet[];
};

export function useStandardWallets<T>({
  filterWallets,
  getExtraWallets,
}: UseWalletsOptions<T>): {
  wallets: T[];
} {
  const [walletsApi] = useState(() => getWallets());

  const [wallets, setWallets] = useState<T[]>(() => {
    const initialWallets = filterWallets(walletsApi.get());
    const extraWallets = getExtraWallets
      ? filterWallets(getExtraWallets())
      : [];

    return [...initialWallets, ...extraWallets];
  });

  useEffect(() => {
    const listeners = [
      walletsApi.on("register", (...newWallets) =>
        setWallets((prevWallets) => [
          ...prevWallets,
          ...filterWallets(newWallets),
        ])
      ),
      walletsApi.on("unregister", (...newWallets) =>
        setWallets((prevWallets) =>
          prevWallets.filter((prevWallet) =>
            newWallets.some((wallet) => wallet === prevWallet)
          )
        )
      ),
    ];

    return () => listeners.forEach((off) => off());
  }, [filterWallets, getExtraWallets, walletsApi]);

  return {
    wallets,
  };
}
